import React from "react";
import {
  Control,
  FieldErrors,
  UseFormRegister,
  UseFormWatch,
} from "react-hook-form";

import {
  TeamMemberNamesAndEmailSchemaType,
  TermsAndConditionsSchemaType,
} from "~/common/utils/signUpSchemas";
import { useOnSignUpTeamMemberStore } from "~/providers/stores/useOnSignUpTeamMemberStore";
import { TeamMemberSignUpSchemaType } from "~/providers/utils/teamMemberSignUpSchema";
import { MobileNumberPage } from "./MobileNumberPage";
import { MobileNumberVerificationPage } from "./MobileNumberVerificationPage";
import { TeamMemberNameAndEmailForm } from "./TeamMemberNameAndEmailForm";
import {
  PasswordsSchemaType,
  TeamMemberPasswordForm,
} from "./TeamMemberPasswordForm";
import { TermsAndConditionsPage } from "./TermsAndConditionsPage";

interface SignUpTeamMemberRenderProps {
  onNext: (schemaName: keyof TeamMemberSignUpSchemaType) => Promise<void>;
  onRetry?: () => void;
  handleRegistration: () => Promise<void>;
  handleGoBack: (schemaName: keyof TeamMemberSignUpSchemaType) => void;
  register: UseFormRegister<TeamMemberSignUpSchemaType>;
  control: Control<TeamMemberSignUpSchemaType>;
  watch: UseFormWatch<TeamMemberSignUpSchemaType>;
  errors: FieldErrors<TeamMemberSignUpSchemaType>;
  isPending?: boolean;
  canResendAt: number;
}

export const SignUpTeamMemberRender = ({
  errors,
  handleGoBack,
  handleRegistration,
  onNext,
  onRetry,
  canResendAt,
  register,
  control,
  watch,
  isPending,
}: SignUpTeamMemberRenderProps) => {
  const { currentStep } = useOnSignUpTeamMemberStore();
  const progressBarStep = currentStep + 1;

  const steps = [
    (totalSteps: number) => (
      <TeamMemberNameAndEmailForm
        onNext={() => onNext("namesAndEmail")}
        totalSteps={totalSteps}
        register={(data: keyof TeamMemberNamesAndEmailSchemaType) =>
          register(`namesAndEmail.${data}`)
        }
        errors={errors.namesAndEmail}
        step={progressBarStep}
      />
    ),
    (totalSteps: number) => (
      <TermsAndConditionsPage
        onNext={() => onNext("termsAndConditions")}
        totalSteps={totalSteps}
        step={progressBarStep}
        handleGoBack={() => handleGoBack("termsAndConditions")}
        register={(data: keyof TermsAndConditionsSchemaType) =>
          register(`termsAndConditions.${data}`)
        }
        errors={errors.termsAndConditions}
      />
    ),
    (totalSteps: number) => (
      <MobileNumberPage
        onNext={() => onNext("mobileNumber")}
        totalSteps={totalSteps}
        step={progressBarStep}
        handleGoBack={() => handleGoBack("mobileNumber")}
        register={register}
        errors={errors.mobileNumber}
        pendingRequest={isPending}
      />
    ),
    (totalSteps: number) => (
      <MobileNumberVerificationPage
        onNext={() => onNext("verification")}
        totalSteps={totalSteps}
        step={progressBarStep}
        handleGoBack={() => handleGoBack("verification")}
        errors={errors.verification}
        canResendAt={canResendAt}
        onRetry={onRetry}
        codeFieldKey="verification.code"
        pendingRequest={isPending}
        control={control}
      />
    ),
    (totalSteps: number) => (
      <TeamMemberPasswordForm
        totalSteps={totalSteps}
        enteredPassword={watch("password.password")}
        handleGoBack={() => handleGoBack("password")}
        register={(data: keyof PasswordsSchemaType) =>
          register(`password.${data}`)
        }
        onNext={handleRegistration}
        errors={errors.password}
        step={progressBarStep}
        pendingRequest={isPending}
      />
    ),
  ];

  return steps[currentStep](steps.length);
};
